import { useState, useEffect } from "react";
import { useCurrency } from "./useCurrency";
import { getUserCurrency, convertToUserCurrency } from "../utils/userCurrency";

/**
 * Hook for managing user-specific currency operations
 * @param {string} userId - User ID to fetch currency preferences for
 * @returns {Object} User currency utilities and state
 */
export const useUserCurrency = (userId) => {
  const {
    systemCurrency,
    currencyRates,
    isLoaded: contextLoaded,
    getExchangeRate,
    getCurrencySymbol,
    formatAmount,
  } = useCurrency();

  const [userCurrencyData, setUserCurrencyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch user currency data
  useEffect(() => {
    const fetchUserCurrency = async () => {
      if (!userId) {
        setUserCurrencyData(null);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await getUserCurrency(userId);
        setUserCurrencyData(data);
      } catch (err) {
        console.error("Error fetching user currency:", err);
        setError(err);
        setUserCurrencyData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUserCurrency();
  }, [userId]);

  // Get effective currency for this user
  const getEffectiveCurrency = () => {
    if (userCurrencyData?.preferredCurrency) {
      return userCurrencyData.preferredCurrency;
    }
    if (systemCurrency) {
      return systemCurrency;
    }
    return "USD";
  };

  const effectiveCurrency = getEffectiveCurrency();

  // Convert USD to user's effective currency
  const convertUSDToUserCurrency = (usdAmount) => {
    if (!usdAmount || usdAmount === 0) return 0;
    if (effectiveCurrency === "USD") return usdAmount;

    const rate = getExchangeRate(effectiveCurrency);
    return usdAmount * rate;
  };

  // Format amount with user's currency
  const formatUserAmount = (usdAmount, precision = 2) => {
    const convertedAmount = convertUSDToUserCurrency(usdAmount);
    return formatAmount(convertedAmount, effectiveCurrency);
  };

  // Get currency status info
  const getCurrencyStatus = () => {
    if (
      userCurrencyData?.preferredCurrency &&
      !userCurrencyData?.usingSystemDefault
    ) {
      return {
        type: "user",
        currency: userCurrencyData.preferredCurrency,
        label: "User Preferred",
        color: "blue",
        icon: "👤",
      };
    } else if (userCurrencyData?.usingSystemDefault) {
      return {
        type: "system",
        currency: effectiveCurrency,
        label: "System Default",
        color: "green",
        icon: "🌐",
      };
    } else {
      return {
        type: "fallback",
        currency: effectiveCurrency,
        label: "Fallback",
        color: "gray",
        icon: "⚙️",
      };
    }
  };

  // Check if user has a preferred currency set
  const hasUserPreference = () => {
    return !!(
      userCurrencyData?.preferredCurrency &&
      !userCurrencyData?.usingSystemDefault
    );
  };

  // Refresh user currency data
  const refreshUserCurrency = async () => {
    if (!userId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await getUserCurrency(userId);
      setUserCurrencyData(data);
    } catch (err) {
      console.error("Error refreshing user currency:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  return {
    // State
    userCurrencyData,
    effectiveCurrency,
    loading: loading || !contextLoaded,
    error,
    isLoaded: contextLoaded && !loading,

    // Utilities
    convertUSDToUserCurrency,
    formatUserAmount,
    getCurrencyStatus,
    hasUserPreference,
    getEffectiveCurrency,

    // Actions
    refreshUserCurrency,

    // Currency info
    currencySymbol: getCurrencySymbol(effectiveCurrency),
    exchangeRate: getExchangeRate(effectiveCurrency),
  };
};

export default useUserCurrency;
